import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ValidatedInput } from '../../../components/design-system/auth';
import { useAccountApi } from '../../../common/hooks/use-account-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import { AccountHeading, AccountNotice, AccountPage, isValidEmail } from './components/AccountPage';

/**
 * Ask for a reset link. The answer is the same whether or not the address has
 * an account, so this page never says which it was.
 */
const ForgotPasswordPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const accountApi = useAccountApi();

    // The login page hands over what was typed there (in state, not the URL).
    const [email, setEmail] = useState<string>((location.state as any)?.email || '');
    const [check, setCheck] = useState(false);
    const [sending, setSending] = useState(false);
    const [sentTo, setSentTo] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const submit = async () => {
        setCheck(true);
        setError(null);
        if (!isValidEmail(email) || sending) return;
        setSending(true);
        try {
            await accountApi.forgotPassword(email.trim());
            setSentTo(email.trim());
        } catch (e) {
            const { status } = await readApiError(e);
            setError(status === 429 ? t('ACCOUNT.COMMON.TOO_MANY') : t('ACCOUNT.COMMON.GENERIC_ERROR'));
        } finally {
            setSending(false);
        }
    };

    if (sentTo) {
        return (
            <AccountPage>
                <AccountNotice tone="info" title={t('ACCOUNT.FORGOT.SENT_TITLE')} testId="forgot-sent">
                    {t('ACCOUNT.FORGOT.SENT', { email: sentTo })}
                </AccountNotice>
                <Button variant="outline" className="h-11 w-full" onClick={() => navigate('/login')}>
                    {t('ACCOUNT.COMMON.BACK_TO_LOGIN')}
                </Button>
            </AccountPage>
        );
    }

    return (
        <AccountPage>
            <AccountHeading title={t('ACCOUNT.FORGOT.TITLE')} subtitle={t('ACCOUNT.FORGOT.SUBTITLE')} />
            {error && <AccountNotice tone="error">{error}</AccountNotice>}
            <ValidatedInput
                label={`${t('ACCOUNT.COMMON.EMAIL_LABEL')}*`}
                placeholder={t('ACCOUNT.COMMON.EMAIL_PLACEHOLDER')}
                value={email}
                onChange={setEmail}
                onKeyDown={(e: any) => e.key === 'Enter' && submit()}
                type="email"
                autoComplete="email"
                error={check && !isValidEmail(email)}
                errorMessage={t('ACCOUNT.COMMON.EMAIL_INVALID')}
                startIcon={<Mail />}
            />
            <Button className="mt-6 h-11 w-full" onClick={submit} disabled={sending}>
                {t('ACCOUNT.FORGOT.SUBMIT')}
            </Button>
            <Button variant="ghost" className="mt-2 h-11 w-full" onClick={() => navigate('/login')}>
                {t('ACCOUNT.COMMON.BACK_TO_LOGIN')}
            </Button>
        </AccountPage>
    );
};

export default ForgotPasswordPage;
